import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ORG_TYPES = [
  "Destination touristique", "Hébergement", "Institution", "Compagnie aérienne",
  "DMC — Destination Management Company", "Agence ou consultant", "Autre",
];
const NEEDS = [
  "Centraliser les données", "Analyser les marchés", "Détecter des opportunités",
  "Piloter des campagnes", "Identifier des partenaires",
  "Utiliser l'IA sur mes données", "Autre",
];
const CONSENT_LABEL =
  "J’accepte que Lodge & Co utilise ces informations pour répondre à ma demande de démonstration.";

const EMPTY = {
  contact_name: "", email: "", organization: "", organization_type: "",
  primary_need: "", message: "", consent: false, company_website: "",
};

function readUtm() {
  try {
    const q = new URLSearchParams(window.location.search);
    return {
      utm_source: q.get("utm_source") || undefined,
      utm_medium: q.get("utm_medium") || undefined,
      utm_campaign: q.get("utm_campaign") || undefined,
    };
  } catch {
    return {};
  }
}

const inputCls =
  "h-[50px] w-full rounded-lg border border-slate-200 bg-[#FBFAF5] px-3.5 text-sm text-[#17201A] outline-none transition-colors focus:border-[#5C9873] focus:bg-white focus:ring-2 focus:ring-[#5C9873]/20";

const Fld = ({ label, htmlFor, children }) => (
  <div className="space-y-1.5">
    <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </label>
    {children}
  </div>
);

export const DemoRequestModal = ({
  renderTrigger = true,
  controlledOpen,
  onControlledOpenChange,
  prefill,
  contextLabel,
  source = "login_landing",
} = {}) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onControlledOpenChange?.(v);
    else setInternalOpen(v);
  };
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const firstFieldRef = useRef(null);
  const utmRef = useRef({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openModal = () => setOpen(true);

  const closeModal = () => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  // Initialize the form each time the modal opens (both controlled & uncontrolled),
  // applying any session prefill and frontend context passed by the caller.
  useEffect(() => {
    if (!open) return;
    utmRef.current = readUtm();
    setDone(false);
    setError("");
    setForm({
      ...EMPTY,
      organization: prefill?.organization || "",
      email: prefill?.email || "",
      message: contextLabel || "",
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => firstFieldRef.current && firstFieldRef.current.focus(), 40);
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeModal();
      } else if (e.key === "Tab") {
        const nodes = panelRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!nodes || nodes.length === 0) return;
        const list = Array.from(nodes).filter((n) => !n.disabled && n.offsetParent !== null);
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit =
    form.contact_name.trim() &&
    form.email.trim() &&
    form.organization.trim() &&
    form.organization_type &&
    form.primary_need &&
    form.consent &&
    !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      const payload = {
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim(),
        organization_type: form.organization_type,
        primary_need: form.primary_need,
        message: form.message.trim() || undefined,
        consent: true,
        company_website: form.company_website || undefined,
        source: source,
        ...utmRef.current,
      };
      const { data } = await api.post("/prospects/demo-request", payload);
      setDone(true);
      toast.success(data?.message || "Votre demande a bien été enregistrée.");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Une erreur est survenue. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {renderTrigger && (
        <button
          ref={triggerRef}
          type="button"
          onClick={openModal}
          data-testid="cta-demo-request"
          className="mt-9 inline-flex h-14 w-full max-w-sm items-center justify-center gap-3 rounded-full bg-[#1E3D2B] px-9 text-sm font-semibold text-white shadow-lg shadow-black/25 transition-colors hover:bg-[#2C5540] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C6A662] focus-visible:ring-offset-2 focus-visible:ring-offset-[#12281C] sm:w-auto"
        >
          Demande de démonstration
          <span aria-hidden="true">→</span>
        </button>
      )}

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur-sm sm:items-center sm:p-6"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
          data-testid="demo-request-overlay"
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-modal-title"
            data-testid="demo-request-modal"
            className="relative my-auto max-h-[calc(100dvh-2rem)] w-full max-w-[660px] overflow-y-auto rounded-[22px] border border-slate-200/70 bg-[#FCFBF7] p-6 text-left shadow-[0_24px_70px_-20px_rgba(15,35,25,0.35)] sm:max-h-[calc(100dvh-3rem)] sm:p-11"
          >
            <button
              type="button"
              onClick={closeModal}
              aria-label="Fermer"
              data-testid="demo-modal-close"
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5C9873]"
            >
              <X size={18} />
            </button>

            {done ? (
              <div className="py-4 text-center" data-testid="demo-request-success">
                <Logo className="mx-auto block h-11 w-auto" />
                <CheckCircle2 className="mx-auto mt-5 h-11 w-11 text-[#3E7256]" strokeWidth={1.5} />
                <h2 id="demo-modal-title" className="mt-4 font-heading text-2xl font-bold text-[#1E3D2B]">
                  Demande envoyée
                </h2>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
                  Merci, votre demande a bien été enregistrée. Notre équipe vous contactera prochainement.
                </p>
                <button
                  type="button"
                  onClick={closeModal}
                  data-testid="demo-modal-done"
                  className="mt-7 inline-flex h-[52px] items-center justify-center rounded-xl bg-[#1E3D2B] px-8 text-sm font-semibold text-white transition-colors hover:bg-[#2C5540] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5C9873] focus-visible:ring-offset-2"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <Logo className="mx-auto block h-10 w-auto sm:h-11" />
                  <h2 id="demo-modal-title" className="mt-5 font-heading text-2xl font-bold tracking-tight text-[#1E3D2B] sm:text-[1.7rem]">
                    Demander une démonstration
                  </h2>
                  <p className="mx-auto mt-2.5 max-w-md text-sm leading-relaxed text-slate-500">
                    Présentez-nous votre organisation et vos besoins. Nous préparerons une démonstration
                    personnalisée.
                  </p>
                </div>

                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="demo-request-form" noValidate>
                  {/* Honeypot (hidden from users & AT) */}
                  <div aria-hidden="true" className="hidden">
                    <label htmlFor="company_website">Company website</label>
                    <input
                      id="company_website" type="text" tabIndex={-1} autoComplete="off"
                      value={form.company_website} onChange={(e) => set("company_website", e.target.value)}
                    />
                  </div>

                  <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2">
                    <Fld label="Nom et prénom" htmlFor="dm-name">
                      <input ref={firstFieldRef} id="dm-name" data-testid="demo-name" required
                        value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} />
                    </Fld>
                    <Fld label="E-mail professionnel" htmlFor="dm-email">
                      <input id="dm-email" data-testid="demo-email" type="email" required
                        value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                    </Fld>
                    <Fld label="Organisation" htmlFor="dm-org">
                      <input id="dm-org" data-testid="demo-organization" required
                        value={form.organization} onChange={(e) => set("organization", e.target.value)} className={inputCls} />
                    </Fld>
                    <Fld label="Type d’organisation" htmlFor="dm-type">
                      <Select value={form.organization_type} onValueChange={(v) => set("organization_type", v)}>
                        <SelectTrigger id="dm-type" data-testid="demo-org-type" className="h-[50px] bg-[#FBFAF5]">
                          <SelectValue placeholder="Sélectionner…" />
                        </SelectTrigger>
                        <SelectContent className="z-[110]">
                          {ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Fld>
                  </div>

                  <Fld label="Besoin principal" htmlFor="dm-need">
                    <Select value={form.primary_need} onValueChange={(v) => set("primary_need", v)}>
                      <SelectTrigger id="dm-need" data-testid="demo-need" className="h-[50px] bg-[#FBFAF5]">
                        <SelectValue placeholder="Sélectionner…" />
                      </SelectTrigger>
                      <SelectContent className="z-[110]">
                        {NEEDS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Fld>

                  <Fld label="Message (facultatif)" htmlFor="dm-msg">
                    <textarea id="dm-msg" data-testid="demo-message" rows={3}
                      value={form.message} onChange={(e) => set("message", e.target.value)}
                      className={`${inputCls} h-auto py-3 leading-relaxed`} />
                  </Fld>

                  <label className="flex items-start gap-3 rounded-xl border border-slate-200/70 bg-white/60 px-4 py-3.5 text-sm leading-relaxed text-slate-600">
                    <input type="checkbox" data-testid="demo-consent" checked={form.consent}
                      onChange={(e) => set("consent", e.target.checked)}
                      className="mt-0.5 h-[18px] w-[18px] shrink-0 rounded border-slate-300 text-[#1E3D2B] focus:ring-[#5C9873]" />
                    <span>{CONSENT_LABEL}</span>
                  </label>

                  {error && (
                    <div data-testid="demo-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit" data-testid="demo-submit" disabled={!canSubmit}
                    className="flex h-[52px] w-full items-center justify-center rounded-xl bg-[#1E3D2B] text-sm font-semibold text-white transition-colors hover:bg-[#2C5540] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5C9873] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#1E3D2B]/70"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer ma demande"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
